import { LegacyRef, useCallback } from "react"
import { Splide as SplideCore, Options } from "@splidejs/splide"
import {
  Splide,
  SplideEventHandlers,
  SplideProps,
} from "@splidejs/react-splide"
import { Box, SxProps, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import isFunction from "lodash/isFunction"

import "@splidejs/react-splide/css"

import { getVisiblePaginationIndexRange } from "utils/carousel"

export interface IExtendOptions extends Options {
  limitPaginations?: number
}
export interface ICarouselProps extends Omit<SplideProps, "options"> {
  children: React.ReactNode
  sx?: SxProps
  options?: IExtendOptions
  splideRef?: LegacyRef<Splide>
}

export const Carousel: React.FC<ICarouselProps> = ({
  children,
  options: optionsProps = {},
  onUpdated: onUpdatedProp,
  sx = {},
  onPaginationUpdated,
  onReady,
  onActive,
  onInactive,
  splideRef,
  ...restProps
}) => {
  const { limitPaginations, ...options } = optionsProps
  const theme = useTheme()
  const { root } = theme.components[`VuiCarousel`]?.styleOverrides || {}
  const isThemeReady = theme.mixins.ready

  const handleReady: SplideEventHandlers["onReady"] = useCallback(
    (splide) => {
      //pause all video that is not in active slide
      splide.root
        .querySelectorAll(".splide__slide:not(.is-active)")
        .forEach((e) => {
          const video = e.querySelector("video")
          if (video) video.pause()
        })

      const activeSlide = splide.root.querySelector(".is-active")
      if (activeSlide) {
        const activeVideo = activeSlide.querySelector("video")
        if (activeVideo) {
          activeVideo.muted = true
          activeVideo.play()
        }
      }
      if (isFunction(onReady)) onReady(splide)
    },
    [onReady]
  )

  const handleActive: SplideEventHandlers["onActive"] = useCallback(
    (splide, slide) => {
      const video = slide.slide.querySelector("video")
      if (video) {
        video.muted = true
        video.play()
      }

      if (isFunction(onActive)) onActive(splide, slide)
    },
    [onActive]
  )

  const handleInactive: SplideEventHandlers["onInactive"] = useCallback(
    (splide, slide) => {
      const video = slide.slide.querySelector("video")
      if (video) video.pause()

      if (isFunction(onInactive)) onInactive(splide, slide)
    },
    [onInactive]
  )

  const handlePaginationUpdated: SplideEventHandlers["onPaginationUpdated"] =
    useCallback(
      (splide, ...rest) => {
        if (limitPaginations) {
          const allPaginationButton = splide.root.querySelectorAll(
            " .splide__pagination__page"
          )
          const currentIndex = splide.index
          const totalPaginations = splide.length

          const { startIndex, endIndex } = getVisiblePaginationIndexRange(
            currentIndex,
            totalPaginations,
            limitPaginations
          )

          allPaginationButton.forEach((dot, index) => {
            const shouldShow = index >= startIndex && index <= endIndex
              ; (dot as HTMLElement).style.display = shouldShow
                ? "inline-block"
                : "none"
          })
        }

        if (isFunction(onPaginationUpdated))
          onPaginationUpdated(splide, ...rest)
      },
      [limitPaginations, onPaginationUpdated]
    )

  const handleOptionsUpdated: SplideEventHandlers["onUpdated"] = useCallback(
    (splide, options) => {
      const { isPaused, play, pause } = splide.Components.Autoplay
      const isIdle = splide.state.is(SplideCore.STATES.IDLE)
      const isAutoplay = options.autoplay
      splide.refresh()
      /**
       * Handle autoplay case when the option is updated from another component
       * [@Doc](https://splidejs.com/guides/options/#autoplay)
       */
      isAutoplay && isIdle && isPaused() ? play() : pause()
      if (isFunction(onUpdatedProp)) onUpdatedProp(splide, options)
    },
    [onUpdatedProp]
  )

  const direction = theme.direction

  if (!isThemeReady) return null

  return (
    <Box sx={deepmerge(root, sx)}>
      <Splide
        aria-label="Carousel"
        ref={splideRef}
        {...restProps}
        options={{
          rewind: true,
          perPage: 1,
          gap: "5rem",
          padding: { left: 24, right: 24 },
          pagination: false,
          arrows: false,
          direction: direction,
          ...options,
        }}
        onUpdated={handleOptionsUpdated}
        onPaginationUpdated={handlePaginationUpdated}
        onReady={handleReady}
        onActive={handleActive}
        onInactive={handleInactive}
      >
        {children}
      </Splide>
    </Box>
  )
}
